<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Configure properties</h1>

                    <a :href="`/#/admin/integrations/pipelines/details/${tid}/${id}`" type="button" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        <span>Back</span>
                    </a>
                </div>
                <div class="col-lg-5 col-sm-6 text-right">
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div class="container">
            <div class="row">
                <div class="col" style="height: 200px; overflow-y: auto;">
                    <h5>Dataset available</h5>
                    <div v-for="p in availableDatasetPropertyNames" class="input-group input-group-sm mb-1">
                        <input type="text" class="form-control" :placeholder="p" disabled>
                        <div class="input-group-append">
                            <button @click="handleIncludePipelineDatasetPropertyCommandV1(p)" class="btn btn-outline-primary" type="button">Include</button>
                        </div>
                    </div>
                </div>

                <div class="col">
                    <h5>Dataset included</h5>
                    <div v-for="p in datasetProperties" class="input-group input-group-sm mb-1">
                        <input type="text" class="form-control" :placeholder="p" disabled>
                        <div class="input-group-append">
                            <button @click="handleExcludePipelineDatasetPropertyCommandV1(p)" class="btn btn-outline-primary" type="button">Exclude</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col" style="height: 200px; overflow-y: auto;">
                    <h5>Connector available</h5>
                    <div v-for="p in availableConnectorPropertyNames" class="input-group input-group-sm mb-1">
                        <input type="text" class="form-control" :placeholder="p" disabled>
                        <div class="input-group-append">
                            <button @click="handleIncludePipelineConnectorPropertyCommandV1(p)" class="btn btn-outline-primary" type="button">Include</button>
                        </div>
                    </div>
                </div>

                <div class="col">
                    <h5>Connector included</h5>
                    <div v-for="p in connectorProperties" class="input-group input-group-sm mb-1">
                        <input type="text" class="form-control" :placeholder="p" disabled>
                        <div class="input-group-append">
                            <button @click="handleExcludePipelineConnectorPropertyCommandV1(p)" class="btn btn-outline-primary" type="button">Exclude</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
    import { useId, useOrganizationUnitId } from '@common/composables'
    import { useAdminConfigurePipelinePropertiesPage } from '@Integrations/Pipelines/pages-core'

    document.title = "Configure properties"

    const tid = useOrganizationUnitId()
    const id = useId()

    const datasetId = ref()
    const connectorId = ref()

    const {
        useCurrentUserQuery,
        usePipelineDetailsQuery,
        useDatasetPropertyNamesQuery,
        useConnectorPropertyNamesQuery,
        useExcludePipelineDatasetPropertyCommand,
        useIncludePipelineDatasetPropertyCommand,
        useExcludePipelineConnectorPropertyCommand,
        useIncludePipelineConnectorPropertyCommand
    } = useAdminConfigurePipelinePropertiesPage()

    const { modelCurrentUserQueryV1, runCurrentUserQueryV1 } = useCurrentUserQuery()
    const userId = computed(() => modelCurrentUserQueryV1.value?.UserId)

    const { runPipelineDetailsQueryV1, modelPipelineDetailsQueryV1 } = usePipelineDetailsQuery(id)
    const datasetProperties = computed<string>(() => modelPipelineDetailsQueryV1.value?.DatasetProperties)
    const connectorProperties = computed<string>(() => modelPipelineDetailsQueryV1.value?.ConnectorProperties)

    const { runDatasetPropertyNamesQueryV1, modelDatasetPropertyNamesQueryV1 } = useDatasetPropertyNamesQuery(datasetId)
    const datasetPropertyNames = computed<string>(() => modelDatasetPropertyNamesQueryV1.value?.PropertyNames || [])

    const { runConnectorPropertyNamesQueryV1, modelConnectorPropertyNamesQueryV1 } = useConnectorPropertyNamesQuery(connectorId)
    const connectorPropertyNames = computed<string>(() => modelConnectorPropertyNamesQueryV1.value?.PropertyNames || [])

    const { invokeIncludePipelineDatasetPropertyCommandV1, succeedIncludePipelineDatasetPropertyCommandV1 } = useIncludePipelineDatasetPropertyCommand(id, tid, userId)
    const { invokeExcludePipelineDatasetPropertyCommandV1, succeedExcludePipelineDatasetPropertyCommandV1 } = useExcludePipelineDatasetPropertyCommand(id, tid, userId)

    const { invokeIncludePipelineConnectorPropertyCommandV1, succeedIncludePipelineConnectorPropertyCommandV1 } = useIncludePipelineConnectorPropertyCommand(id, tid, userId)
    const { invokeExcludePipelineConnectorPropertyCommandV1, succeedExcludePipelineConnectorPropertyCommandV1 } = useExcludePipelineConnectorPropertyCommand(id, tid, userId)

    const availableDatasetPropertyNames = computed(() => datasetPropertyNames.value.filter(p => !datasetProperties.value.includes(p)))
    const availableConnectorPropertyNames = computed(() => connectorPropertyNames.value.filter(p => !connectorProperties.value.includes(p)))

    onMounted(() => {
        runCurrentUserQueryV1()
        runPipelineDetailsQueryV1()
    })

    watch(connectorId, () => {
        runConnectorPropertyNamesQueryV1()
    })

    watch(datasetId, () => {
        runDatasetPropertyNamesQueryV1()
    })

    watch(modelPipelineDetailsQueryV1, () => {
        datasetId.value = modelPipelineDetailsQueryV1.value.DatasetId
        connectorId.value = modelPipelineDetailsQueryV1.value.ConnectorId
    })

    watch([
        succeedIncludePipelineDatasetPropertyCommandV1,
        succeedExcludePipelineDatasetPropertyCommandV1,
        succeedIncludePipelineConnectorPropertyCommandV1,
        succeedExcludePipelineConnectorPropertyCommandV1
    ], () => runPipelineDetailsQueryV1())

    function handleIncludePipelineDatasetPropertyCommandV1(propertyName: string) {
        invokeIncludePipelineDatasetPropertyCommandV1({ PropertyName: propertyName })
    }

    function handleExcludePipelineDatasetPropertyCommandV1(propertyName: string) {
        invokeExcludePipelineDatasetPropertyCommandV1({ PropertyName: propertyName })
    }

    function handleIncludePipelineConnectorPropertyCommandV1(propertyName: string) {
        invokeIncludePipelineConnectorPropertyCommandV1({ PropertyName: propertyName })
    }

    function handleExcludePipelineConnectorPropertyCommandV1(propertyName: string) {
        invokeExcludePipelineConnectorPropertyCommandV1({ PropertyName: propertyName })
    }
</script>